import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bell, User, Search, ChevronDown, Clock, LogOut } from 'lucide-react';
import { useAuthStore, useAlertStore } from '../store';
import { alertService } from '../services/api';
import { toast } from 'react-toastify';

const TopBar = () => {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const { unreadCount, setUnreadCount } = useAlertStore();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => new Date());

  useEffect(() => {
    loadUnreadCount();
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => window.clearInterval(timer);
  }, []);

  const formattedTime = currentTime.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'America/Sao_Paulo',
  });

  const formattedDate = currentTime.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    weekday: 'short',
    timeZone: 'America/Sao_Paulo',
  });

  const loadUnreadCount = async () => {
    try {
      const response = await alertService.getUnreadCount();
      setUnreadCount(response.data.data.count);
    } catch (error) {
      console.error('Erro ao carregar alertas não lidos:', error);
    }
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    toast.success('Logout realizado com sucesso');
  };

  return (
    <header className="bg-[#07111f] border-b border-primary-300/15 px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Search Bar */}
        <div className="flex-1 max-w-xl">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar veiculos, placas..."
              className="w-full bg-[#030a14] border border-primary-300/15 rounded-lg pl-11 pr-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
            />
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-4 ml-6">
          <div className="hidden items-center gap-2 rounded-lg border border-primary-300/15 bg-[#030a14] px-3 py-2 text-right sm:flex">
            <Clock className="h-5 w-5 text-primary-400" />
            <div className="leading-tight">
              <p className="font-mono text-sm font-semibold text-white tabular-nums">{formattedTime}</p>
              <p className="hidden text-[11px] uppercase text-dark-400 md:block">{formattedDate}</p>
            </div>
          </div>

          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 rounded-lg hover:bg-primary-500/10 transition-colors"
            >
              <Bell className="w-6 h-6 text-dark-400" />
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 w-5 h-5 bg-danger-500 rounded-full text-xs text-white flex items-center justify-center font-bold">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowNotifications(false)}
                />
                <div className="absolute right-0 mt-2 w-80 bg-[#07111f] rounded-lg shadow-xl border border-primary-300/15 z-20 animate-fade-in">
                  <div className="p-4 border-b border-primary-300/15">
                    <h3 className="font-semibold text-white">Notificacoes</h3>
                    {unreadCount > 0 && (
                      <p className="text-sm text-dark-400 mt-1">
                        {unreadCount} {unreadCount === 1 ? 'nova notificacao' : 'novas notificacoes'}
                      </p>
                    )}
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {unreadCount === 0 ? (
                      <div className="p-8 text-center text-dark-400">
                        <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>Nenhuma notificacao</p>
                      </div>
                    ) : (
                      <div className="p-2">
                        <div className="text-center text-dark-400 text-sm p-4">
                          Carregue a página de alertas para ver detalhes
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-primary-500/10 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-primary flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-white">{user?.name || 'Usuario'}</p>
                <p className="text-xs text-dark-400">{user?.role === 'admin' ? 'Administrador' : 'Usuario'}</p>
              </div>
              <ChevronDown className="w-4 h-4 text-dark-400" />
            </button>

            {/* User Menu Dropdown */}
            {showUserMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowUserMenu(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-[#07111f] rounded-lg shadow-xl border border-primary-300/15 z-20 animate-fade-in">
                  <div className="p-2">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 rounded-lg text-dark-300 hover:bg-primary-500/10 hover:text-white transition-colors"
                    >
                      Meu Perfil
                    </Link>
                    <Link
                      to="/settings"
                      className="block px-4 py-2 rounded-lg text-dark-300 hover:bg-primary-500/10 hover:text-white transition-colors"
                    >
                      Configuracoes
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="mt-1 flex w-full items-center gap-2 rounded-lg px-4 py-2 text-left text-danger-400 transition-colors hover:bg-primary-500/10"
                    >
                      <LogOut className="h-4 w-4" />
                      Sair
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default TopBar;
